import { ConnectedIcon, DisconnectedIcon } from "@/components/icons/connection";
import { cn } from "@/lib/utils";
import { ReadyState } from "react-use-websocket";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover/popover";
import { useRef, useState } from "react";
import { App } from "@/signal/message";
import { useTranslation } from "react-i18next";
import { useSignalStatus } from "./use-signal-status";

import { useCompleteWOOSerializedSignal } from "@/signal/use-complete-woo-serialized-signal";
import { useEngravingCompletedSignal } from "@/signal/use-engraving-completed-signal";
import { useMessageToHalpSignal } from "@/signal/use-message-to-halp-signal";
import { usePrintingCompletedSignal } from "@/signal/use-printing-completed-signal";
import { useProductsWaitingForValidationSignal } from "@/signal/use-products-waiting-for-validation-signal";
import { useStartWOOSerializedSignal } from "@/signal/use-start-woo-serialized-signal";
import { useTaskStatusChanged } from "@/signal/use-task-status-changed-signal";
import { useMarkingCompletedSignal } from "../../signal/use-marking-completed-signal";
import {
  StatusItem,
  StatusState,
} from "@/components/ui/status-item/stauts-item";

export function SignalStatus() {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [popover, setPopover] = useState(false);
  const { connecting, connected, disconnected, taskWs, appu4dmWs, network } =
    useSignalStatus();

  const channels = [
    useTaskStatusChanged(),
    useMessageToHalpSignal(),
    useStartWOOSerializedSignal(),
    useCompleteWOOSerializedSignal(),
    useProductsWaitingForValidationSignal(),
    useEngravingCompletedSignal(),
    usePrintingCompletedSignal(),
    useMarkingCompletedSignal(),
  ];

  const statusDisplays = [
    {
      app: App.Task,
      readyState: taskWs.readyState,
    },
    {
      app: App.AppU4DM,
      readyState: appu4dmWs.readyState,
    },
  ];

  const ConnectedStatusIcon = connected ? ConnectedIcon : DisconnectedIcon;

  return (
    <Popover open={popover}>
      <PopoverAnchor
        ref={anchorRef}
        role="button"
        onClick={() => setPopover((s) => !s)}
        className={cn(
          "cursor-pointer flex items-center justify-center shadow-sm gap-2 rounded-md text-sm text-white p-2 text-nowrap text-start outline-none w-[73px] ml-auto",
          {
            "bg-warning": connecting,
            "bg-destructive": disconnected,
            "bg-header-chip": connected,
          }
        )}
      >
        <ConnectedStatusIcon />
      </PopoverAnchor>

      <PopoverContent
        onPointerDownOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) return;
          setPopover(false);
        }}
      >
        <div className="flex flex-col gap-y-2">
          <StatusItem
            label={t("signalStatus.network")}
            status={network.online ? StatusState.Ok : StatusState.Err}
          />

          {statusDisplays.map((statusItem) => (
            <div key={statusItem.app}>
              <StatusItem
                label={statusItem.app}
                status={readyStateToStatus(
                  statusItem.readyState,
                  network.online
                )}
              />
              {channels
                .filter((c) => c.app === statusItem.app)
                .map((channel) => (
                  <StatusItem
                    className="ml-2 text-xs"
                    key={channel.channel}
                    label={channel.channel}
                    status={
                      network.online && channel.acknowledged
                        ? StatusState.Ok
                        : StatusState.Err
                    }
                  />
                ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function readyStateToStatus(readyState: ReadyState, online: boolean) {
  if (!online) {
    return StatusState.Err;
  }
  switch (readyState) {
    case ReadyState.UNINSTANTIATED:
    case ReadyState.CONNECTING:
      return StatusState.Warn;
    case ReadyState.OPEN:
      return StatusState.Ok;
    case ReadyState.CLOSING:
    case ReadyState.CLOSED:
      return StatusState.Err;
  }
}
